import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { ThemeCookieManager } from '@/shared/utils/cookies';
import { glassVariants } from '@/shared/components/ui/GlassTheme.tsx';
import { fantasyVariants } from '@/shared/components/ui/FantasyTheme.tsx';

// Theme types
export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type ThemeVariant = 'glass' | 'fantasy';

// Base theme configuration interface
export interface BaseThemeConfig {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  variant: ThemeVariant;
  setTheme: (theme: ThemeMode) => void;
  setVariant: (variant: ThemeVariant) => void;
  toggleTheme: () => void;
}

// Theme variant definitions
export const themeVariants = {
  glass: glassVariants,
  fantasy: fantasyVariants,
} as const;

// Function to apply CSS custom properties that override DaisyUI defaults
function applyThemeVariables(root: HTMLElement, config: { variant: ThemeVariant; resolvedTheme: ResolvedTheme }) {
  // Get button text colors from our theme system
  const buttonTextPrimary = getThemeClass('buttonText', 'primary', config);
  const buttonTextSecondary = getThemeClass('buttonText', 'secondary', config);
  const buttonTextGhost = getThemeClass('buttonText', 'ghost', config);
  const buttonTextDisabled = getThemeClass('buttonText', 'disabled', config);
  const textPrimary = getThemeClass('text', 'primary', config);
  const textSecondary = getThemeClass('text', 'secondary', config);
  
  // Extract color values from Tailwind classes and set as CSS variables
  const colorMap: Record<string, string> = {
    '!text-white': '#ffffff',
    '!text-black': '#000000',
    '!text-gray-900': '#111827',
    '!text-gray-700': '#374151',
    '!text-gray-500': '#6b7280',
    '!text-gray-400': '#9ca3af',
    '!text-gray-300': '#d1d5db',
    '!text-gray-100': '#f3f4f6',
    '!text-cyan-400': '#22d3ee',
    '!text-cyan-300': '#67e8f9',
    '!text-cyan-100': '#cffafe',
    '!text-cyan-50': '#ecfeff',
    'text-white': '#ffffff',
    'text-black': '#000000',
    'text-gray-900': '#111827',
    'text-gray-700': '#374151',
    'text-gray-500': '#6b7280',
    'text-gray-400': '#9ca3af',
    'text-gray-300': '#d1d5db',
    'text-gray-100': '#f3f4f6',
    'text-cyan-400': '#22d3ee',
    'text-cyan-300': '#67e8f9',
    'text-cyan-100': '#cffafe',
    'text-cyan-50': '#ecfeff',
  };
  
  // Function to extract color from class string
  const extractColor = (classString: string): string => {
    const classes = classString.split(' ');
    for (const cls of classes) {
      if (colorMap[cls]) {
        return colorMap[cls];
      }
    }
    return colorMap['!text-gray-900'] || '#111827'; // fallback
  };
  
  // Set CSS custom properties that will override DaisyUI
  root.style.setProperty('--theme-btn-primary-text', extractColor(buttonTextPrimary));
  root.style.setProperty('--theme-btn-secondary-text', extractColor(buttonTextSecondary));
  root.style.setProperty('--theme-btn-ghost-text', extractColor(buttonTextGhost));
  root.style.setProperty('--theme-btn-disabled-text', extractColor(buttonTextDisabled));
  root.style.setProperty('--theme-text-primary', extractColor(textPrimary));
  root.style.setProperty('--theme-text-secondary', extractColor(textSecondary));
}

// Create theme context
const ThemeContext = createContext<BaseThemeConfig | undefined>(undefined);

export interface ThemeMasterProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeMode;
  defaultVariant?: ThemeVariant;
  storageKey?: string;
}

export function ThemeMasterProvider({
  children,
  defaultTheme = 'light',
  defaultVariant = 'glass',
}: ThemeMasterProviderProps) {
  // Initialize with stored values immediately if available
  const getInitialTheme = (): ThemeMode => {
    try {
      const stored = ThemeCookieManager.loadTheme();
      if (stored?.mode && ['light', 'dark', 'system'].includes(stored.mode)) {
        return stored.mode as ThemeMode;
      }
    } catch {
      // Fall through to default
    }
    return defaultTheme;
  };

  const getInitialVariant = (): ThemeVariant => {
    try {
      const stored = ThemeCookieManager.loadTheme();
      const validVariants = Object.keys(themeVariants) as ThemeVariant[];
      if (stored?.variant && validVariants.includes(stored.variant as ThemeVariant)) {
        return stored.variant as ThemeVariant;
      }
    } catch {
      // Fall through to default
    }
    return defaultVariant;
  };

  const [mode, setMode] = useState<ThemeMode>(getInitialTheme());
  const [variant, setVariant] = useState<ThemeVariant>(getInitialVariant());
  
  // Get system theme preference
  const getSystemTheme = useCallback((): ResolvedTheme => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light'; // Default when system preference unavailable (SSR / no window)
  }, []);

  // Calculate initial resolved theme
  const getInitialResolvedTheme = (): ResolvedTheme => {
    const initialMode = getInitialTheme();
    if (initialMode === 'system') {
      return getSystemTheme();
    }
    return initialMode;
  };

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(getInitialResolvedTheme());

  // Resolve theme based on mode
  const resolveTheme = useCallback((themeMode: ThemeMode): ResolvedTheme => {
    if (themeMode === 'system') {
      return getSystemTheme();
    }
    return themeMode;
  }, [getSystemTheme]);

  // Guards the backend-mirroring effect below from re-saving the exact
  // value it just read while server hydration is in flight/applying.
  const hydratingFromServer = useRef(false);

  // Update resolved theme when mode changes or system preference changes
  useEffect(() => {
    const newResolvedTheme = resolveTheme(mode);
    setResolvedTheme(newResolvedTheme);

    // Apply theme to document
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(newResolvedTheme);
      
      // Set data attributes for theme and variant
      root.setAttribute('data-theme', newResolvedTheme);
      root.setAttribute('data-variant', variant);
      
      // Apply CSS custom properties to override DaisyUI defaults
      const config = { variant, resolvedTheme: newResolvedTheme };
      applyThemeVariables(root, config);
    }

    // Save to cookies - fast, synchronous, available before login is even
    // known (this provider sits above UserStateProvider in the tree, so it
    // can't use that context). This is a same-device cache, not the source
    // of truth once a real account is involved - see the DB sync below.
    try {
      ThemeCookieManager.saveTheme(mode, variant);
    } catch {
      // Silently fail if cookie saving fails
    }

    // Mirror to the backend when authenticated, so theme/variant follow the
    // account across devices instead of staying stuck in a per-browser
    // cookie. Fire-and-forget: a failed save here shouldn't block anything
    // visual, and the cookie write above already covers this device.
    if (!hydratingFromServer.current) {
      import('@/features/user/service').then(({ userService }) => {
        if (!userService.isAuthenticated()) return;
        import('@/shared/services/userApiService').then(({ userApiService }) => {
          userApiService.updateUserSettings({
            appearance_settings: { theme: mode, variant },
          }).catch(() => {
            // Silently fail - cookie already has this device covered.
          });
        });
      });
    }
  }, [mode, variant, resolveTheme]);

  // One-time hydration from the backend on mount: if this is a fresh
  // device/browser (no cookie, or a stale one), pull the last theme/variant
  // saved from ANY device instead of falling back to defaultTheme/
  // defaultVariant. Runs once; login/logout mid-session don't currently
  // re-trigger it, matching how the cookie itself only ever changes via
  // explicit user action.
  useEffect(() => {
    let cancelled = false;
    import('@/features/user/service').then(({ userService }) => {
      if (!userService.isAuthenticated()) return;
      import('@/shared/services/userApiService').then(({ userApiService }) => {
        userApiService.getUserSettings().then((response) => {
          if (cancelled || !response.success) return;
          const appearance = response.data?.settings?.appearance;
          if (!appearance) return;
          hydratingFromServer.current = true;
          if (appearance.theme && ['light', 'dark', 'system'].includes(appearance.theme)) {
            setMode(appearance.theme as ThemeMode);
          }
          if (appearance.variant && (Object.keys(themeVariants) as string[]).includes(appearance.variant)) {
            setVariant(appearance.variant as ThemeVariant);
          }
          // Release the guard on the next tick, after the state updates
          // above have run through the save effect once.
          setTimeout(() => {
            hydratingFromServer.current = false;
          }, 0);
        }).catch(() => {
          // Silently fail - cookie/default already applied.
        });
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load Cinzel (Fantasy's display serif) only while Fantasy is active, so
  // Glass users never fetch it.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const linkId = 'fantasy-theme-font';
    const existing = document.getElementById(linkId);
    if (variant === 'fantasy') {
      if (!existing) {
        const link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700&display=swap';
        document.head.appendChild(link);
      }
    } else if (existing) {
      existing.remove();
    }
  }, [variant]);

  // Listen for system theme changes
  useEffect(() => {
    if (mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setResolvedTheme(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode, getSystemTheme]);

  const setTheme = (newTheme: ThemeMode) => {
    setMode(newTheme);
  };

  const toggleTheme = () => {
    if (mode === 'system') {
      setMode('light');
    } else if (mode === 'light') {
      setMode('dark');
    } else {
      setMode('light');
    }
  };

  const value: BaseThemeConfig = {
    mode,
    resolvedTheme,
    variant,
    setTheme,
    setVariant,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Hook to use theme
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within ThemeMasterProvider');
  }
  return context;
}

// Safe hook that doesn't throw when used outside provider
export function useSafeTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    // Return fallback values when provider is not available
    return {
      mode: 'light' as ThemeMode,
      resolvedTheme: 'light' as ResolvedTheme,
      variant: 'glass' as ThemeVariant,
      setTheme: () => {},
      setVariant: () => {},
      toggleTheme: () => {},
    };
  }
  return context;
}

// Utility function to get theme class names
export function getThemeClass(
  category: keyof typeof themeVariants.glass,
  subCategory?: string,
  themeConfig?: { variant: ThemeVariant; resolvedTheme: ResolvedTheme }
): string {
  const config = themeConfig || { variant: 'glass', resolvedTheme: 'light' };
  
  const variantObj = themeVariants[config.variant] as Record<string, unknown>;
  
  if (!variantObj || !variantObj[category]) {
    return '';
  }
  
  const categoryObj = variantObj[category] as Record<string, unknown>;
  
  if (subCategory && typeof categoryObj === 'object' && categoryObj !== null && subCategory in categoryObj) {
    const subObj = (categoryObj as Record<string, unknown>)[subCategory];
    if (typeof subObj === 'object' && subObj !== null && config.resolvedTheme in subObj) {
      return (subObj as Record<string, string>)[config.resolvedTheme];
    }
    return typeof subObj === 'string' ? subObj : '';
  }
  
  if (typeof categoryObj === 'object' && categoryObj !== null && config.resolvedTheme in categoryObj) {
    return (categoryObj as Record<string, string>)[config.resolvedTheme];
  }
  
  return '';
}

// Helper function to get theme orbs array
export function getThemeOrbs(
  themeConfig?: { variant: ThemeVariant; resolvedTheme: ResolvedTheme }
): string[] {
  const config = themeConfig || { variant: 'glass', resolvedTheme: 'light' };
  
  const variantObj = themeVariants[config.variant] as Record<string, unknown>;
  
  if (!variantObj || !variantObj.orbs) {
    return [];
  }
  
  const orbsObj = variantObj.orbs as Record<string, string[]>;
  
  if (typeof orbsObj === 'object' && orbsObj !== null && config.resolvedTheme in orbsObj) {
    return orbsObj[config.resolvedTheme] || [];
  }
  
  return [];
}

// Theme utilities hook
export function useThemeClasses() {
  const { resolvedTheme, variant } = useTheme();
  
  // Ensure we have valid values, fallback to defaults if needed
  const safeResolvedTheme = resolvedTheme || 'light';
  const safeVariant = variant || 'glass';
  const config = { variant: safeVariant, resolvedTheme: safeResolvedTheme };
  
  return {
    // Layout components
    background: getThemeClass('background', undefined, config),
    backgroundFixed: getThemeClass('backgroundFixed', undefined, config),
    layoutShellHome: getThemeClass('layoutShellHome', undefined, config),
    card: getThemeClass('card', undefined, config),
    header: getThemeClass('header', undefined, config),
    footer: getThemeClass('footer', undefined, config),
    form: getThemeClass('form', undefined, config),
    sidebar: getThemeClass('sidebar', undefined, config),
    section: getThemeClass('section', undefined, config),
    container: getThemeClass('container', undefined, config),
    panel: getThemeClass('panel', undefined, config),
    rangeSlider: getThemeClass('rangeSlider', undefined, config),
    
    // Interactive components
    input: getThemeClass('input', undefined, config),
    inputError: getThemeClass('input', 'error', config),
    avatar: getThemeClass('avatar', undefined, config),
    avatarButton: getThemeClass('avatarButton', undefined, config),
    dropdown: getThemeClass('dropdown', undefined, config),
    
    // Form controls
    checkbox: getThemeClass('checkbox', undefined, config),
    radio: getThemeClass('radio', undefined, config),
    toggle: getThemeClass('toggle', undefined, config),
    select: getThemeClass('select', undefined, config),
    textarea: getThemeClass('textarea', undefined, config),
    
    // Button variants
    buttonPrimary: getThemeClass('button', 'primary', config),
    buttonSecondary: getThemeClass('button', 'secondary', config),
    buttonGhost: getThemeClass('button', 'ghost', config),
    buttonDisabled: getThemeClass('button', 'disabled', config),
    
    // Button text variants
    buttonTextPrimary: getThemeClass('buttonText', 'primary', config),
    buttonTextSecondary: getThemeClass('buttonText', 'secondary', config),
    buttonTextGhost: getThemeClass('buttonText', 'ghost', config),
    buttonTextDisabled: getThemeClass('buttonText', 'disabled', config),
    
    // Text variants
    textPrimary: getThemeClass('text', 'primary', config),
    textSecondary: getThemeClass('text', 'secondary', config),
    textMuted: getThemeClass('text', 'muted', config),
    textLabel: getThemeClass('text', 'label', config),
    textPlaceholder: getThemeClass('text', 'placeholder', config),
    textHeading: getThemeClass('text', 'heading', config),
    textAccent: getThemeClass('text', 'accent', config),
    textError: getThemeClass('text', 'error', config),
    textErrorHeading: getThemeClass('text', 'errorHeading', config),
    textSuccess: getThemeClass('text', 'success', config),
    textInfo: getThemeClass('text', 'info', config),
    textInfoHeading: getThemeClass('text', 'infoHeading', config),
    textStrikethrough: getThemeClass('text', 'strikethrough', config),
    textMultiline: getThemeClass('text', 'multiline', config),
    
    // Modal variants
    modalOverlay: getThemeClass('modal', 'overlay', config),
    modalContent: getThemeClass('modal', 'content', config),
    
    // Alert variants
    alertInfo: getThemeClass('alert', 'info', config),
    alertSuccess: getThemeClass('alert', 'success', config),
    alertWarning: getThemeClass('alert', 'warning', config),
    alertError: getThemeClass('alert', 'error', config),
    
    // Navigation variants
  navMain: getThemeClass('nav', 'main', config),
  navItem: getThemeClass('nav', 'item', config),
  navItemActive: getThemeClass('nav', 'itemActive', config),
  navDropdown: getThemeClass('nav', 'dropdown', config),
  navFunToolsChevron: getThemeClass('nav', 'funToolsChevron', config),
  navFunToolsChevronOpen: getThemeClass('nav', 'funToolsChevronOpen', config),
    
    // Status indicators
    progressTrack: getThemeClass('progressBar', 'track', config),
    progressFill: getThemeClass('progressBar', 'fill', config),
    skeleton: getThemeClass('skeleton', undefined, config),
    spinner: getThemeClass('spinner', undefined, config),
    
    // Navigation elements  
    // breadcrumb: getThemeClass('breadcrumb', undefined, config),
    // menu: getThemeClass('menu', undefined, config),
    // menuItem: getThemeClass('menuItem', undefined, config),
    
    // Interactive elements
    tooltip: getThemeClass('tooltip', undefined, config),
    popover: getThemeClass('popover', undefined, config),
    drawer: getThemeClass('drawer', undefined, config),
    
    // Data display
    table: getThemeClass('table', undefined, config),
    tableHeader: getThemeClass('tableHeader', undefined, config),
    tableRow: getThemeClass('tableRow', undefined, config),
    list: getThemeClass('list', undefined, config),
    listItem: getThemeClass('listItem', undefined, config),
    
    // Loading and feedback variants
    loadingSpinnerSmall: getThemeClass('loadingSpinner', 'small', config),
    loadingSpinnerMedium: getThemeClass('loadingSpinner', 'medium', config),
    loadingSpinnerLarge: getThemeClass('loadingSpinner', 'large', config),
    warningAlert: getThemeClass('warningAlert', undefined, config),
    warningText: getThemeClass('warningText', undefined, config),
    modalBackdrop: getThemeClass('modalBackdrop', undefined, config),
    errorBoundaryContainer: getThemeClass('errorBoundary', 'container', config),
    errorBoundaryCard: getThemeClass('errorBoundary', 'card', config),
    errorBoundaryBackground: getThemeClass('errorBoundary', 'background', config),
    imageBorder: getThemeClass('imageBorder', undefined, config),
    mediaPendingFrame: getThemeClass('media', 'pendingFrame', config),
    mediaLoadingBox: getThemeClass('media', 'loadingBox', config),
    mediaHeroBackdrop: getThemeClass('media', 'heroBackdrop', config),
    mediaHeroBackdropVideo: getThemeClass('media', 'heroBackdropVideo', config),
    mediaHeroBackdropScrim: getThemeClass('media', 'heroBackdropScrim', config),
    mediaHeroOrbLayer: getThemeClass('media', 'heroOrbLayer', config),
    mediaHeroContentLayer: getThemeClass('media', 'heroContentLayer', config),
    placeholderText: getThemeClass('placeholderText', undefined, config),
    themeToggleHover: getThemeClass('themeToggleHover', undefined, config),
    
    // Separator
    separator: getThemeClass('separator', undefined, config),
    
    // Badge variants
    badge: getThemeClass('badge', undefined, config),
    
    // Background decoration orbs
    orbs: getThemeOrbs(config),
    
    // Page wrapper for main body glass slab
    pageWrapper: getThemeClass('pageWrapper', undefined, config),

    settingsToggleTrackOn: getThemeClass('dashboardSettings', 'toggleTrackOn', config),
    settingsToggleTrackOff: getThemeClass('dashboardSettings', 'toggleTrackOff', config),
    settingsToggleKnob: getThemeClass('dashboardSettings', 'toggleKnob', config),
    settingsToggleFocusRing: getThemeClass('dashboardSettings', 'toggleFocusRing', config),
    settingsRadioCardSelected: getThemeClass('dashboardSettings', 'radioCardSelected', config),
    settingsRadioCardUnselected: getThemeClass('dashboardSettings', 'radioCardUnselected', config),
    settingsRadioOuterChecked: getThemeClass('dashboardSettings', 'radioOuterChecked', config),
    settingsRadioOuterUnchecked: getThemeClass('dashboardSettings', 'radioOuterUnchecked', config),
    settingsRadioInnerDot: getThemeClass('dashboardSettings', 'radioInnerDot', config),
    settingsPasswordInputFocus: getThemeClass('dashboardSettings', 'passwordInputFocus', config),
    settingsPasswordMeterTrack: getThemeClass('dashboardSettings', 'passwordMeterTrack', config),
    settingsPasswordMeterWeak: getThemeClass('dashboardSettings', 'passwordMeterWeak', config),
    settingsPasswordMeterMedium: getThemeClass('dashboardSettings', 'passwordMeterMedium', config),
    settingsPasswordMeterStrong: getThemeClass('dashboardSettings', 'passwordMeterStrong', config),
  };
}

// Safe theme classes hook - reads the real variant from context when a
// provider is present (matching useThemeClasses()'s behavior), falling back
// to glass only when there's genuinely no provider to read from. Previously
// this discarded context.variant unconditionally, which meant no component
// using this hook (the vast majority of the app) could ever render anything
// but glass regardless of what variant was actually selected.
export function useSafeThemeClasses() {
  const context = useContext(ThemeContext);

  const safeResolvedTheme = context?.resolvedTheme || 'light';
  const safeVariant: ThemeVariant = context?.variant || 'glass';
  const config = { variant: safeVariant, resolvedTheme: safeResolvedTheme };
  
  return {
    // Layout components
    background: getThemeClass('background', undefined, config),
    backgroundFixed: getThemeClass('backgroundFixed', undefined, config),
    layoutShellHome: getThemeClass('layoutShellHome', undefined, config),
    card: getThemeClass('card', undefined, config),
    header: getThemeClass('header', undefined, config),
    footer: getThemeClass('footer', undefined, config),
    form: getThemeClass('form', undefined, config),
    sidebar: getThemeClass('sidebar', undefined, config),
    section: getThemeClass('section', undefined, config),
    container: getThemeClass('container', undefined, config),
    panel: getThemeClass('panel', undefined, config),
    slider: getThemeClass('slider', undefined, config),
    rangeSlider: getThemeClass('rangeSlider', undefined, config),
    
    // Interactive components
    input: getThemeClass('input', undefined, config),
    inputError: getThemeClass('input', 'error', config),
    avatar: getThemeClass('avatar', undefined, config),
    avatarButton: getThemeClass('avatarButton', undefined, config),
    dropdown: getThemeClass('dropdown', undefined, config),
    
    // Form controls
    checkbox: getThemeClass('checkbox', undefined, config),
    radio: getThemeClass('radio', undefined, config),
    toggle: getThemeClass('toggle', undefined, config),
    select: getThemeClass('select', undefined, config),
    textarea: getThemeClass('textarea', undefined, config),
    
    // Button variants
    buttonPrimary: getThemeClass('button', 'primary', config),
    buttonSecondary: getThemeClass('button', 'secondary', config),
    buttonGhost: getThemeClass('button', 'ghost', config),
    buttonDisabled: getThemeClass('button', 'disabled', config),
    
    // Button text variants
    buttonTextPrimary: getThemeClass('buttonText', 'primary', config),
    buttonTextSecondary: getThemeClass('buttonText', 'secondary', config),
    buttonTextGhost: getThemeClass('buttonText', 'ghost', config),
    buttonTextDisabled: getThemeClass('buttonText', 'disabled', config),
    
    // Text variants
    textPrimary: getThemeClass('text', 'primary', config),
    textSecondary: getThemeClass('text', 'secondary', config),
    textMuted: getThemeClass('text', 'muted', config),
    textLabel: getThemeClass('text', 'label', config),
    textPlaceholder: getThemeClass('text', 'placeholder', config),
    textHeading: getThemeClass('text', 'heading', config),
    textAccent: getThemeClass('text', 'accent', config),
    textError: getThemeClass('text', 'error', config),
    textErrorHeading: getThemeClass('text', 'errorHeading', config),
    textSuccess: getThemeClass('text', 'success', config),
    textInfo: getThemeClass('text', 'info', config),
    textInfoHeading: getThemeClass('text', 'infoHeading', config),
    textStrikethrough: getThemeClass('text', 'strikethrough', config),
    textMultiline: getThemeClass('text', 'multiline', config),
    
    // Modal variants
    modalOverlay: getThemeClass('modal', 'overlay', config),
    modalContent: getThemeClass('modal', 'content', config),
    
    // Alert variants
    alertInfo: getThemeClass('alert', 'info', config),
    alertSuccess: getThemeClass('alert', 'success', config),
    alertWarning: getThemeClass('alert', 'warning', config),
    alertError: getThemeClass('alert', 'error', config),
    
    // Navigation variants
  navMain: getThemeClass('nav', 'main', config),
  navItem: getThemeClass('nav', 'item', config),
  navItemActive: getThemeClass('nav', 'itemActive', config),
  navDropdown: getThemeClass('nav', 'dropdown', config),
  navFunToolsChevron: getThemeClass('nav', 'funToolsChevron', config),
  navFunToolsChevronOpen: getThemeClass('nav', 'funToolsChevronOpen', config),
    
    // Status indicators
    progressTrack: getThemeClass('progressBar', 'track', config),
    progressFill: getThemeClass('progressBar', 'fill', config),
    skeleton: getThemeClass('skeleton', undefined, config),
    spinner: getThemeClass('spinner', undefined, config),
    
    // Navigation elements  
    // breadcrumb: getThemeClass('breadcrumb', undefined, config),
    // menu: getThemeClass('menu', undefined, config),
    // menuItem: getThemeClass('menuItem', undefined, config),
    
    // Interactive elements
    tooltip: getThemeClass('tooltip', undefined, config),
    popover: getThemeClass('popover', undefined, config),
    drawer: getThemeClass('drawer', undefined, config),
    
    // Data display
    table: getThemeClass('table', undefined, config),
    tableHeader: getThemeClass('tableHeader', undefined, config),
    tableRow: getThemeClass('tableRow', undefined, config),
    list: getThemeClass('list', undefined, config),
    listItem: getThemeClass('listItem', undefined, config),
    
    // Loading and feedback variants
    loadingSpinnerSmall: getThemeClass('loadingSpinner', 'small', config),
    loadingSpinnerMedium: getThemeClass('loadingSpinner', 'medium', config),
    loadingSpinnerLarge: getThemeClass('loadingSpinner', 'large', config),
    warningAlert: getThemeClass('warningAlert', undefined, config),
    warningText: getThemeClass('warningText', undefined, config),
    modalBackdrop: getThemeClass('modalBackdrop', undefined, config),
    errorBoundaryContainer: getThemeClass('errorBoundary', 'container', config),
    errorBoundaryCard: getThemeClass('errorBoundary', 'card', config),
    errorBoundaryBackground: getThemeClass('errorBoundary', 'background', config),
    imageBorder: getThemeClass('imageBorder', undefined, config),
    mediaPendingFrame: getThemeClass('media', 'pendingFrame', config),
    mediaLoadingBox: getThemeClass('media', 'loadingBox', config),
    mediaHeroBackdrop: getThemeClass('media', 'heroBackdrop', config),
    mediaHeroBackdropVideo: getThemeClass('media', 'heroBackdropVideo', config),
    mediaHeroBackdropScrim: getThemeClass('media', 'heroBackdropScrim', config),
    mediaHeroOrbLayer: getThemeClass('media', 'heroOrbLayer', config),
    mediaHeroContentLayer: getThemeClass('media', 'heroContentLayer', config),
    placeholderText: getThemeClass('placeholderText', undefined, config),
    themeToggleHover: getThemeClass('themeToggleHover', undefined, config),
    
    // Separator
    separator: getThemeClass('separator', undefined, config),
    
    // Badge variants
    badge: getThemeClass('badge', undefined, config),
    
    // Background decoration orbs
    orbs: getThemeOrbs(config),
    
    // Page wrapper for main body glass slab
    pageWrapper: getThemeClass('pageWrapper', undefined, config),

    settingsToggleTrackOn: getThemeClass('dashboardSettings', 'toggleTrackOn', config),
    settingsToggleTrackOff: getThemeClass('dashboardSettings', 'toggleTrackOff', config),
    settingsToggleKnob: getThemeClass('dashboardSettings', 'toggleKnob', config),
    settingsToggleFocusRing: getThemeClass('dashboardSettings', 'toggleFocusRing', config),
    settingsRadioCardSelected: getThemeClass('dashboardSettings', 'radioCardSelected', config),
    settingsRadioCardUnselected: getThemeClass('dashboardSettings', 'radioCardUnselected', config),
    settingsRadioOuterChecked: getThemeClass('dashboardSettings', 'radioOuterChecked', config),
    settingsRadioOuterUnchecked: getThemeClass('dashboardSettings', 'radioOuterUnchecked', config),
    settingsRadioInnerDot: getThemeClass('dashboardSettings', 'radioInnerDot', config),
    settingsPasswordInputFocus: getThemeClass('dashboardSettings', 'passwordInputFocus', config),
    settingsPasswordMeterTrack: getThemeClass('dashboardSettings', 'passwordMeterTrack', config),
    settingsPasswordMeterWeak: getThemeClass('dashboardSettings', 'passwordMeterWeak', config),
    settingsPasswordMeterMedium: getThemeClass('dashboardSettings', 'passwordMeterMedium', config),
    settingsPasswordMeterStrong: getThemeClass('dashboardSettings', 'passwordMeterStrong', config),
  };
}

// Theme toggle components
export function SimpleThemeToggle({ className = '' }: { className?: string }) {
  const context = useContext(ThemeContext);
  const { textPrimary, navItem } = useSafeThemeClasses();
  
  const getLabel = () => {
    if (!context) return 'Switch Theme';
    switch (context.mode) {
      case 'light':
        return 'Light Mode';
      case 'dark':
        return 'Dark Mode';
      case 'system':
        return 'System Theme';
      default:
        return 'Switch Theme';
    }
  };
  
  const handleToggle = () => {
    if (context) {
      context.toggleTheme();
    }
  };
  
  return (
    <button
      onClick={handleToggle}
      className={`w-full text-left px-4 py-2 rounded-lg transition-all duration-200 ${navItem} ${textPrimary} ${className}`}
    >
      <span className="flex items-center gap-2">
        <span className="text-lg">
          {context?.mode === 'light' ? '☀️' : context?.mode === 'dark' ? '🌙' : '🔄'}
        </span>
        {getLabel()}
      </span>
    </button>
  );
}

export function ThemeToggle({ className = '' }: { className?: string }) {
  const { mode, toggleTheme, resolvedTheme } = useTheme();
  const { buttonSecondary } = useThemeClasses();
  
  const getIcon = () => {
    switch (mode) {
      case 'light':
        return '☀️';
      case 'dark':
        return '🌙';
      case 'system':
        // For system mode, show icon based on resolved theme
        return resolvedTheme === 'dark' ? '🌙' : '☀️';
      default:
        return '🌙'; // Default to moon for dark mode
    }
  };
  
  const getLabel = () => {
    switch (mode) {
      case 'light':
        return 'Light mode';
      case 'dark':
        return 'Dark mode';
      case 'system':
        return 'System mode';
      default:
        return 'Light mode';
    }
  };
  
  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-xl transition-all duration-300 hover:scale-105 ${buttonSecondary} ${className}`}
      title={getLabel()}
      aria-label={getLabel()}
    >
      <span className="text-lg">{getIcon()}</span>
    </button>
  );
}

// Variant selector component
export function ThemeVariantSelector({ className = '' }: { className?: string }) {
  const { variant, setVariant } = useTheme();
  const { buttonSecondary, buttonPrimary } = useThemeClasses();
  
  const variants: { key: ThemeVariant; label: string; icon: string }[] = [
    { key: 'glass', label: 'Glass', icon: '💫' },
    { key: 'fantasy', label: 'Fantasy', icon: '📜' },
  ];
  
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {variants.map((v) => (
        <button
          key={v.key}
          onClick={() => setVariant(v.key)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            variant === v.key 
              ? `${buttonPrimary} shadow-lg` 
              : buttonSecondary
          }`}
          title={`Switch to ${v.label} theme`}
        >
          <span className="mr-1">{v.icon}</span>
          {v.label}
        </button>
      ))}
    </div>
  );
}

export const ThemeProvider = ThemeMasterProvider;
export type ThemeProviderProps = ThemeMasterProviderProps;

/** Hex palette for Three.js / canvas (derived from resolved light/dark) */
export interface TechNerdThreePalette {
  sceneBackground: number;
  sceneFog: number;
  coreBase: number;
  coreEmissive: number;
  blueAmplification: number;
  particlePrimary: number;
  lightKey: number;
  lightFill: number;
  lightRim: number;
}

export function getTechNerdThreePalette(resolvedTheme: ResolvedTheme): TechNerdThreePalette {
  if (resolvedTheme === 'dark') {
    return {
      sceneBackground: 0x0c0c12,
      sceneFog: 0x0c0c12,
      coreBase: 0x6366f1,
      coreEmissive: 0x4f46e5,
      blueAmplification: 0x22d3ee,
      particlePrimary: 0xa78bfa,
      lightKey: 0xffffff,
      lightFill: 0x6b7280,
      lightRim: 0x818cf8,
    };
  }
  return {
    sceneBackground: 0xf4f4f8,
    sceneFog: 0xe8e8f0,
    coreBase: 0x6366f1,
    coreEmissive: 0x818cf8,
    blueAmplification: 0x38bdf8,
    particlePrimary: 0x7c3aed,
    lightKey: 0xffffff,
    lightFill: 0x9ca3af,
    lightRim: 0xc4b5fd,
  };
}
